import React, { useState, useEffect } from 'react';
import { Plus, Edit, Trash2, Eye, EyeOff, Home, Package } from 'lucide-react';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Textarea } from '../../components/ui/textarea';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from '../../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { toast } from 'sonner';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const ProductManagement = () => {
  const [categories, setCategories] = useState([]);
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeView, setActiveView] = useState('categories'); // 'categories' or 'products'
  
  // Category form
  const [categoryForm, setCategoryForm] = useState({
    category_id: '',
    name: '',
    slug: '',
    description: '',
    image_url: '',
    display_order: 0,
    is_active: true
  });
  const [isCategoryFormOpen, setIsCategoryFormOpen] = useState(false);
  
  // Product form
  const [productForm, setProductForm] = useState({
    product_id: '',
    name: '',
    category_id: '',
    description: '',
    image_url: '',
    display_order: 0,
    is_active: true,
    show_on_homepage: false
  });
  const [isProductFormOpen, setIsProductFormOpen] = useState(false);
  
  // Delete confirmation
  const [deleteConfirm, setDeleteConfirm] = useState({
    open: false,
    type: '',
    id: '',
    name: ''
  });
  const [deleting, setDeleting] = useState(false);

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const token = localStorage.getItem('admin_token');
      const config = { headers: { Authorization: `Bearer ${token}` } };

      const [catRes, prodRes] = await Promise.all([
        axios.get(`${BACKEND_URL}/api/admin/categories`, config).catch(() => ({ data: { categories: [] } })),
        axios.get(`${BACKEND_URL}/api/admin/products`, config).catch(() => ({ data: { products: [] } }))
      ]);

      setCategories(catRes.data.categories || []);
      setProducts(prodRes.data.products || []);
    } catch (error) {
      console.error('Error fetching data:', error);
      toast.error('Failed to load data');
    } finally {
      setLoading(false);
    }
  };

  // Category CRUD
  const handleCategorySubmit = async (e) => {
    e.preventDefault();
    try {
      const token = localStorage.getItem('admin_token');
      const config = { headers: { Authorization: `Bearer ${token}` } };
      
      const data = {
        name: categoryForm.name,
        slug: categoryForm.slug || categoryForm.name.toLowerCase().replace(/\s+/g, '-'),
        description: categoryForm.description,
        image_url: categoryForm.image_url,
        display_order: categoryForm.display_order,
        is_active: categoryForm.is_active
      };

      if (categoryForm.category_id) {
        await axios.put(`${BACKEND_URL}/api/admin/categories/${categoryForm.category_id}`, data, config);
        toast.success('Category updated successfully');
      } else {
        await axios.post(`${BACKEND_URL}/api/admin/categories`, data, config);
        toast.success('Category created successfully');
      }
      
      setIsCategoryFormOpen(false);
      setCategoryForm({ category_id: '', name: '', slug: '', description: '', image_url: '', display_order: 0, is_active: true });
      fetchData();
    } catch (error) {
      console.error('Error saving category:', error);
      toast.error('Failed to save category');
    }
  };

  const editCategory = (cat) => {
    setCategoryForm({
      category_id: cat.category_id,
      name: cat.name,
      slug: cat.slug,
      description: cat.description,
      image_url: cat.image_url,
      display_order: cat.display_order || 0,
      is_active: cat.is_active !== false
    });
    setIsCategoryFormOpen(true);
  };

  const deleteCategory = async (categoryId) => {
    setDeleting(true);
    try {
      const token = localStorage.getItem('admin_token');
      await axios.delete(`${BACKEND_URL}/api/admin/categories/${categoryId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Category deleted successfully');
      setDeleteConfirm({ open: false, type: '', id: '', name: '' });
      fetchData();
    } catch (error) {
      console.error('Error deleting category:', error);
      toast.error('Failed to delete category');
    } finally {
      setDeleting(false);
    }
  };

  // Product CRUD
  const handleProductSubmit = async (e) => {
    e.preventDefault();
    try {
      const token = localStorage.getItem('admin_token');
      const config = { headers: { Authorization: `Bearer ${token}` } };
      
      const data = {
        name: productForm.name,
        category_id: productForm.category_id,
        description: productForm.description,
        image_url: productForm.image_url,
        display_order: productForm.display_order,
        is_active: productForm.is_active,
        show_on_homepage: productForm.show_on_homepage
      };

      if (productForm.product_id) {
        await axios.put(`${BACKEND_URL}/api/admin/products/${productForm.product_id}`, data, config);
        toast.success('Product updated successfully');
      } else {
        await axios.post(`${BACKEND_URL}/api/admin/products`, data, config);
        toast.success('Product created successfully');
      }
      
      setIsProductFormOpen(false);
      setProductForm({ product_id: '', name: '', category_id: '', description: '', image_url: '', display_order: 0, is_active: true, show_on_homepage: false });
      fetchData();
    } catch (error) {
      console.error('Error saving product:', error);
      toast.error('Failed to save product');
    }
  };

  const editProduct = (prod) => {
    setProductForm({
      product_id: prod.product_id,
      name: prod.name,
      category_id: prod.category_id,
      description: prod.description || '',
      image_url: prod.image_url || '',
      display_order: prod.display_order || 0,
      is_active: prod.is_active !== false,
      show_on_homepage: prod.show_on_homepage || false
    });
    setIsProductFormOpen(true);
  };

  const deleteProduct = async (productId) => {
    setDeleting(true);
    try {
      const token = localStorage.getItem('admin_token');
      await axios.delete(`${BACKEND_URL}/api/admin/products/${productId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Product deleted successfully');
      setDeleteConfirm({ open: false, type: '', id: '', name: '' });
      fetchData();
    } catch (error) {
      console.error('Error deleting product:', error);
      toast.error('Failed to delete product');
    } finally {
      setDeleting(false);
    }
  };

  const executeDelete = () => {
    if (deleteConfirm.type === 'category') {
      deleteCategory(deleteConfirm.id);
    } else if (deleteConfirm.type === 'product') {
      deleteProduct(deleteConfirm.id);
    }
  };

  const getCategoryName = (categoryId) => {
    const cat = categories.find(c => c.category_id === categoryId);
    return cat ? cat.name : 'Unknown';
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#C9A24D]"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Sub-navigation */}
      <div className="flex gap-4 border-b pb-4">
        <button
          onClick={() => setActiveView('categories')}
          className={`px-4 py-2 rounded-lg font-medium transition-colors ${
            activeView === 'categories' 
              ? 'bg-[#C9A24D] text-[#0B2C4D]' 
              : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
          }`}
        >
          <Package className="w-4 h-4 inline mr-2" />
          Categories ({categories.length})
        </button>
        <button
          onClick={() => setActiveView('products')}
          className={`px-4 py-2 rounded-lg font-medium transition-colors ${
            activeView === 'products' 
              ? 'bg-[#C9A24D] text-[#0B2C4D]' 
              : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
          }`}
        >
          <Package className="w-4 h-4 inline mr-2" />
          Products ({products.length})
        </button>
      </div>

      {/* Categories View */}
      {activeView === 'categories' && (
        <div>
          <div className="flex justify-between items-center mb-6">
            <h3 className="text-xl font-bold text-[#0B2C4D]">Product Categories</h3>
            <Button 
              onClick={() => {
                setCategoryForm({ category_id: '', name: '', slug: '', description: '', image_url: '', display_order: 0, is_active: true });
                setIsCategoryFormOpen(true);
              }}
              className="bg-[#C9A24D] hover:bg-[#b8923e] text-[#0B2C4D]"
            >
              <Plus className="w-4 h-4 mr-2" />
              Add Category
            </Button>
          </div>

          {categories.length === 0 ? (
            <div className="text-center py-12 bg-gray-50 rounded-lg">
              <Package className="w-12 h-12 text-gray-400 mx-auto mb-4" />
              <p className="text-gray-500">No categories yet. Add your first category!</p>
            </div>
          ) : (
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
              {categories.map((cat) => (
                <div key={cat.category_id} className="bg-white border rounded-lg overflow-hidden shadow-sm hover:shadow-md transition-shadow">
                  {cat.image_url && (
                    <img src={cat.image_url} alt={cat.name} className="w-full h-32 object-cover" />
                  )}
                  <div className="p-4">
                    <div className="flex items-center justify-between mb-2">
                      <h4 className="font-semibold text-[#0B2C4D]">{cat.name}</h4>
                      <span className={`px-2 py-0.5 rounded text-xs ${cat.is_active ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-800'}`}>
                        {cat.is_active ? 'Active' : 'Inactive'}
                      </span>
                    </div>
                    <p className="text-sm text-gray-600 mb-3 line-clamp-2">{cat.description}</p>
                    <div className="flex gap-2">
                      <button
                        onClick={() => editCategory(cat)}
                        className="flex-1 text-sm bg-blue-50 hover:bg-blue-100 text-blue-600 py-1.5 px-3 rounded flex items-center justify-center gap-1"
                      >
                        <Edit className="w-3 h-3" /> Edit
                      </button>
                      <button
                        onClick={() => setDeleteConfirm({ open: true, type: 'category', id: cat.category_id, name: cat.name })}
                        className="text-red-600 hover:bg-red-50 p-1.5 rounded"
                      >
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* Products View */}
      {activeView === 'products' && (
        <div>
          <div className="flex justify-between items-center mb-6">
            <h3 className="text-xl font-bold text-[#0B2C4D]">Products</h3>
            <Button 
              onClick={() => {
                setProductForm({ product_id: '', name: '', category_id: '', description: '', image_url: '', display_order: 0, is_active: true, show_on_homepage: false });
                setIsProductFormOpen(true);
              }}
              className="bg-[#C9A24D] hover:bg-[#b8923e] text-[#0B2C4D]"
              disabled={categories.length === 0}
            >
              <Plus className="w-4 h-4 mr-2" />
              Add Product
            </Button>
          </div>

          {categories.length === 0 ? (
            <div className="text-center py-12 bg-yellow-50 rounded-lg border border-yellow-200">
              <p className="text-yellow-700">Please create categories first before adding products.</p>
            </div>
          ) : products.length === 0 ? (
            <div className="text-center py-12 bg-gray-50 rounded-lg">
              <Package className="w-12 h-12 text-gray-400 mx-auto mb-4" />
              <p className="text-gray-500">No products yet. Add your first product!</p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-gray-50 border-b-2 border-[#C9A24D]">
                  <tr>
                    <th className="px-4 py-3 text-left text-sm font-semibold text-[#0B2C4D]">Image</th>
                    <th className="px-4 py-3 text-left text-sm font-semibold text-[#0B2C4D]">Name</th>
                    <th className="px-4 py-3 text-left text-sm font-semibold text-[#0B2C4D]">Category</th>
                    <th className="px-4 py-3 text-left text-sm font-semibold text-[#0B2C4D]">Status</th>
                    <th className="px-4 py-3 text-left text-sm font-semibold text-[#0B2C4D]">Homepage</th>
                    <th className="px-4 py-3 text-left text-sm font-semibold text-[#0B2C4D]">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {products.map((prod) => (
                    <tr key={prod.product_id} className="border-b hover:bg-gray-50">
                      <td className="px-4 py-3">
                        {prod.image_url ? (
                          <img src={prod.image_url} alt={prod.name} className="w-12 h-12 object-cover rounded" />
                        ) : (
                          <div className="w-12 h-12 bg-gray-200 rounded flex items-center justify-center">
                            <Package className="w-6 h-6 text-gray-400" />
                          </div>
                        )}
                      </td>
                      <td className="px-4 py-3 text-sm font-medium">{prod.name}</td>
                      <td className="px-4 py-3 text-sm text-gray-600">{getCategoryName(prod.category_id)}</td>
                      <td className="px-4 py-3">
                        <span className={`px-2 py-0.5 rounded text-xs ${prod.is_active ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-800'}`}>
                          {prod.is_active ? 'Active' : 'Inactive'}
                        </span>
                      </td>
                      <td className="px-4 py-3">
                        {prod.show_on_homepage ? (
                          <Home className="w-4 h-4 text-[#C9A24D]" />
                        ) : (
                          <span className="text-gray-400">-</span>
                        )}
                      </td>
                      <td className="px-4 py-3">
                        <div className="flex gap-2">
                          <button
                            onClick={() => editProduct(prod)}
                            className="text-blue-600 hover:bg-blue-50 p-1.5 rounded"
                          >
                            <Edit className="w-4 h-4" />
                          </button>
                          <button
                            onClick={() => setDeleteConfirm({ open: true, type: 'product', id: prod.product_id, name: prod.name })}
                            className="text-red-600 hover:bg-red-50 p-1.5 rounded"
                          >
                            <Trash2 className="w-4 h-4" />
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}

      {/* Category Form Dialog */}
      <Dialog open={isCategoryFormOpen} onOpenChange={setIsCategoryFormOpen}>
        <DialogContent className="sm:max-w-lg">
          <DialogHeader>
            <DialogTitle>{categoryForm.category_id ? 'Edit Category' : 'Add Category'}</DialogTitle>
          </DialogHeader>
          <form onSubmit={handleCategorySubmit} className="space-y-4">
            <div>
              <label className="block text-sm font-medium mb-1">Category Name *</label>
              <Input
                value={categoryForm.name}
                onChange={(e) => setCategoryForm({...categoryForm, name: e.target.value})}
                required
                placeholder="e.g., Spices & Seasonings"
              />
            </div>
            <div>
              <label className="block text-sm font-medium mb-1">Slug</label>
              <Input
                value={categoryForm.slug}
                onChange={(e) => setCategoryForm({...categoryForm, slug: e.target.value})}
                placeholder="Auto-generated from name"
              />
            </div>
            <div>
              <label className="block text-sm font-medium mb-1">Description</label>
              <Textarea
                value={categoryForm.description}
                onChange={(e) => setCategoryForm({...categoryForm, description: e.target.value})}
                placeholder="Brief description of the category"
                rows={3}
              />
            </div>
            <div>
              <label className="block text-sm font-medium mb-1">Image URL</label>
              <Input
                value={categoryForm.image_url}
                onChange={(e) => setCategoryForm({...categoryForm, image_url: e.target.value})}
                placeholder="https://..."
              />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium mb-1">Display Order</label>
                <Input
                  type="number"
                  value={categoryForm.display_order}
                  onChange={(e) => setCategoryForm({...categoryForm, display_order: parseInt(e.target.value) || 0})}
                />
              </div>
              <div className="flex items-center gap-2 pt-6">
                <input
                  type="checkbox"
                  checked={categoryForm.is_active}
                  onChange={(e) => setCategoryForm({...categoryForm, is_active: e.target.checked})}
                  className="w-4 h-4"
                />
                <label className="text-sm font-medium">Active</label>
              </div>
            </div>
            <Button type="submit" className="w-full bg-[#0B2C4D] hover:bg-[#0a2440]">
              {categoryForm.category_id ? 'Update Category' : 'Create Category'}
            </Button>
          </form>
        </DialogContent>
      </Dialog>

      {/* Product Form Dialog */}
      <Dialog open={isProductFormOpen} onOpenChange={setIsProductFormOpen}>
        <DialogContent className="sm:max-w-lg">
          <DialogHeader>
            <DialogTitle>{productForm.product_id ? 'Edit Product' : 'Add Product'}</DialogTitle>
          </DialogHeader>
          <form onSubmit={handleProductSubmit} className="space-y-4">
            <div>
              <label className="block text-sm font-medium mb-1">Product Name *</label>
              <Input
                value={productForm.name}
                onChange={(e) => setProductForm({...productForm, name: e.target.value})}
                required
                placeholder="e.g., Turmeric Powder"
              />
            </div>
            <div>
              <label className="block text-sm font-medium mb-1">Category *</label>
              <Select 
                value={productForm.category_id} 
                onValueChange={(value) => setProductForm({...productForm, category_id: value})}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Select category" />
                </SelectTrigger>
                <SelectContent>
                  {categories.map((cat) => (
                    <SelectItem key={cat.category_id} value={cat.category_id}>
                      {cat.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <label className="block text-sm font-medium mb-1">Description</label>
              <Textarea
                value={productForm.description}
                onChange={(e) => setProductForm({...productForm, description: e.target.value})}
                placeholder="Product description"
                rows={2}
              />
            </div>
            <div>
              <label className="block text-sm font-medium mb-1">Image URL</label>
              <Input
                value={productForm.image_url}
                onChange={(e) => setProductForm({...productForm, image_url: e.target.value})}
                placeholder="https://..."
              />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium mb-1">Display Order</label>
                <Input
                  type="number"
                  value={productForm.display_order}
                  onChange={(e) => setProductForm({...productForm, display_order: parseInt(e.target.value) || 0})}
                />
              </div>
            </div>
            <div className="flex gap-6">
              <div className="flex items-center gap-2">
                <input
                  type="checkbox"
                  checked={productForm.is_active}
                  onChange={(e) => setProductForm({...productForm, is_active: e.target.checked})}
                  className="w-4 h-4"
                />
                <label className="text-sm font-medium">Active</label>
              </div>
              <div className="flex items-center gap-2">
                <input
                  type="checkbox"
                  checked={productForm.show_on_homepage}
                  onChange={(e) => setProductForm({...productForm, show_on_homepage: e.target.checked})}
                  className="w-4 h-4"
                />
                <label className="text-sm font-medium">Show on Homepage</label>
              </div>
            </div>
            <Button type="submit" className="w-full bg-[#0B2C4D] hover:bg-[#0a2440]">
              {productForm.product_id ? 'Update Product' : 'Create Product'}
            </Button>
          </form>
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <Dialog open={deleteConfirm.open} onOpenChange={(open) => !deleting && setDeleteConfirm({...deleteConfirm, open})}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="text-red-600">Confirm Delete</DialogTitle>
            <DialogDescription className="pt-2">
              Are you sure you want to delete this {deleteConfirm.type}?
              <br />
              <strong className="text-gray-800">{deleteConfirm.name}</strong>
              <br />
              <span className="text-red-500 text-sm">This action cannot be undone.</span>
            </DialogDescription>
          </DialogHeader>
          <DialogFooter className="flex gap-2 sm:gap-0">
            <Button
              variant="outline"
              onClick={() => setDeleteConfirm({ open: false, type: '', id: '', name: '' })}
              disabled={deleting}
            >
              Cancel
            </Button>
            <Button
              variant="destructive"
              onClick={executeDelete}
              disabled={deleting}
              className="bg-red-600 hover:bg-red-700"
            >
              {deleting ? 'Deleting...' : 'Delete'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default ProductManagement;
